import React from 'react';
import classNames from 'classnames';
import { S } from '@mobily/ts-belt';
import { Box, Link, Text } from '@stitch-fix/mode-react';
import {
  GetStartedButton,
  type GetStartedButtonProps,
} from '../GetStartedButton';
import styles from './styles.module.scss';
import {
  CloudinaryVideoPlayer,
  type CloudinaryVideoPlayerProps,
} from '../CloudinaryVideoPlayer';
import { isNonEmptyString } from '../../utils/helpers';
import {
  CloudinaryImageWrapper,
  type CloudinaryImageFields,
} from '../CloudinaryImageWrapper';

type Media =
  | ({
      type: 'image';
    } & CloudinaryImageFields)
  | ({
      type: 'video';
    } & CloudinaryVideoPlayerProps);

type CtaButtonProps =
  | {
      ctaCopy?: GetStartedButtonProps['copy'];
      ctaHref?: GetStartedButtonProps['href'];
      CtaButton?: never;
    }
  | {
      ctaCopy?: never;
      ctaHref?: never;
      CtaButton: React.ReactNode;
    };

type CtaSecondaryButtonProps = {
  CtaSecondaryButton?: React.ReactNode;
};

export type HalfScreenHeroProps = {
  title: string;
  eyebrow?: string;
  description?: string;
  variant?: 'mint' | 'gray' | null;
  signInUrl?: string;
  media: Media;
  contentOrder?: 'textFirst' | 'mediaFirst';
} & CtaButtonProps &
  CtaSecondaryButtonProps;

const getMedia = (media: Media) => {
  if (media.type === 'image') {
    return (
      <CloudinaryImageWrapper
        src={media.src}
        alt={media.alt}
        aspectRatio={media.aspectRatio}
        sources={{
          // based off the current breakpoints sm: 560, md:900, lg: 1140, xl: 1730
          // we want to use the smallest image possible for the screen size
          // on sm and medium screens we display the image full width and the content stacked
          // on lg and xl we display the image half width along side the content
          sm: 560,
          md: 900,
          lg: 700,
          xl: 1200,
        }}
        fetchPriority="high"
        lazy={false}
        cloudinaryAsset={media.cloudinaryAsset}
        sizes={[
          { minWidth: 0, fluidWidth: 100 },
          { minWidth: 900, fluidWidth: 50 },
        ]}
        imageProps={{ fetchPriority: 'high' }}
      />
    );
  }

  if (media.type === 'video') {
    return (
      <CloudinaryVideoPlayer
        videoUrl={media.videoUrl}
        soundControls={media.soundControls}
        videoDescription={media.videoDescription}
        captions={media.captions}
        playPauseButtonSize={media.playPauseButtonSize}
        aspectRatio="16 / 9"
      />
    );
  }

  return <Box />;
};

const shouldGetStartedButtonRender = ({
  ctaCopy,
  ctaHref,
}: Pick<HalfScreenHeroProps, 'ctaCopy' | 'ctaHref'>) => {
  return isNonEmptyString(ctaCopy) || isNonEmptyString(ctaHref);
};

export const HalfScreenHero = ({
  ctaCopy,
  ctaHref,
  CtaButton,
  CtaSecondaryButton,
  description,
  eyebrow,
  title,
  variant,
  signInUrl = '',
  media,
  contentOrder = 'textFirst',
}: HalfScreenHeroProps) => {
  const appliedVariant = variant || 'mint';

  const ctaSection =
    CtaButton && CtaSecondaryButton ? (
      <Box
        className={classNames(styles.contentGrid, styles.buttonWrapper, {
          [styles.buttonSpacer]: !!CtaButton && !!CtaSecondaryButton,
        })}
      >
        {CtaButton ||
          (shouldGetStartedButtonRender({ ctaCopy, ctaHref }) && (
            <GetStartedButton
              copy={ctaCopy}
              href={ctaHref}
              eventLocation="hero"
              variant="filled-staple"
            />
          ))}
        {CtaSecondaryButton}
      </Box>
    ) : (
      CtaButton ||
      (shouldGetStartedButtonRender({ ctaCopy, ctaHref }) && (
        <GetStartedButton
          copy={ctaCopy}
          href={ctaHref}
          eventLocation="hero"
          variant="filled-staple"
        />
      ))
    );

  return (
    <Box
      className={classNames(styles.container, {
        [styles.mint]: appliedVariant === 'mint',
        [styles.gray]: appliedVariant === 'gray',
      })}
    >
      <Box
        className={classNames(styles.contentGrid, {
          [styles.mediaFirst]: contentOrder === 'mediaFirst',
        })}
      >
        <Box className={styles.textWrapper}>
          {eyebrow && (
            <Text setting="eyebrow-medium" spacingBottom>
              {eyebrow}
            </Text>
          )}
          <Text as="h1" setting="title-xlarge" spacingBottom>
            {title}
          </Text>
          {description && (
            <Text
              setting="body-medium"
              className={styles.description}
              spacingBottom
            >
              {description}
            </Text>
          )}
          {ctaSection}
          {S.isNotEmpty(signInUrl) && (
            <Text as="p" setting="body-small-fixed" mt={1}>
              Already have an account?{' '}
              <Link variant="inherit" href={signInUrl}>
                Sign in
              </Link>
            </Text>
          )}
        </Box>
        {getMedia(media)}
      </Box>
    </Box>
  );
};
